import { Upload, BarChart3, RefreshCw } from 'lucide-react';

const scoreTextColor = (s) =>
  s >= 80 ? 'text-emerald-700' : s >= 60 ? 'text-[#2563eb]' : s >= 40 ? 'text-amber-700' : 'text-red-600';

const scoreBarColor = (s) =>
  s >= 80 ? 'bg-emerald-500' : s >= 60 ? 'bg-blue-500' : s >= 40 ? 'bg-amber-500' : 'bg-red-500';

// Defensive helper — always returns a number or null, never an object
const normalizeScore = (value) =>
  typeof value === 'object' && value !== null
    ? (typeof value.score === 'number' ? value.score : null)
    : (typeof value === 'number' ? value : null);

/**
 * ScoringSection — unified LinkedIn + Resume score cards for CandidateDetailPage.
 * Handles all 3 candidate states: V4 sourced, resume-only, brand-new manual.
 * NEVER returns null or renders empty.
 */
export function ScoringSection({ candidate, onOpenScoreReport, onUploadResume, onRescoreResume, onRescoreLinkedIn, rescoreLinkedInLoading }) {

  // ── Normalized score variables ─────────────────────────────────────────────

  // V4 LinkedIn score (object shape)
  const linkedinScore = normalizeScore(candidate?.linkedinscore?.score ?? candidate?.linkedinscore) ?? null;
  const linkedinBreakdown = candidate?.linkedinscore?.breakdown ?? null;
  const hasLinkedin = linkedinScore !== null;

  // V4 Resume score — handles both object {score,breakdown,...} and legacy flat number
  // Also falls back to snake_case resume_score for old DB docs
  const rawResume = candidate?.resumescore ?? candidate?.resume_score ?? null;
  const resumeScore = normalizeScore(rawResume);
  const resumeBreakdown = rawResume?.breakdown || candidate?.resumescorebreakdown || null;
  const resumeSummary = rawResume?.summary || candidate?.resumesummary || null;
  const resumeFlags = rawResume?.flags || candidate?.resumeflags || null;
  const resumeRecommendation = rawResume?.recommendation || candidate?.resumerecommendation || null;
  const hasResume = resumeScore !== null;
  const hasResumeUploaded = !!(candidate?.resumeuploadedat || candidate?.resume_uploaded_at || candidate?.resumerawtext || candidate?.resumetext);

  const recBadgeClass = (rec) => {
    if (rec === 'SHORTLIST') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (rec === 'LONG_SHOT') return 'bg-rose-50 text-rose-700 border-rose-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6"
         data-testid="scoring-section">
      <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#2563eb]" />
        Scoring &amp; Reports
      </h2>

      {/* ── ZONE 1 — Always-visible score cards ─────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-5 mb-6">

        {/* LinkedIn Score Card */}
        <div className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow"
             data-testid="linkedin-score-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-700">LinkedIn Score</h3>
              {hasLinkedin && candidate?.linkedinscore?.breakdown?.flags?.score_error && (
                <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                  Score error
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onRescoreLinkedIn && hasLinkedin && (
                <button
                  type="button"
                  disabled={rescoreLinkedInLoading}
                  onClick={onRescoreLinkedIn}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  data-testid="rescore-linkedin-btn"
                >
                  {rescoreLinkedInLoading
                    ? <><RefreshCw className="w-3 h-3 animate-spin" /> Rescoring…</>
                    : <><RefreshCw className="w-3 h-3" /> Rescore</>
                  }
                </button>
              )}
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">AI Sourcing</span>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <span className={`text-4xl font-bold ${hasLinkedin ? scoreTextColor(linkedinScore) : 'text-slate-300'}`}
                  data-testid="linkedin-score-value">
              {hasLinkedin ? linkedinScore : '—'}
            </span>
            {hasLinkedin && (
              <button
                onClick={() => onOpenScoreReport('linkedin')}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-[#2563eb] rounded-lg text-xs font-semibold transition-colors"
                data-testid="view-linkedin-report-btn"
              >
                <BarChart3 className="w-3.5 h-3.5" /> View Report
              </button>
            )}
          </div>

          {hasLinkedin && candidate?.linkedinscore?.breakdown?.oneliner && (
            <p className="text-xs text-slate-500 italic leading-relaxed">
              {candidate.linkedinscore.breakdown.oneliner}
            </p>
          )}
          {!hasLinkedin && (
            <p className="text-xs text-slate-400 mt-1">
              Available after V4 LinkedIn sourcing
            </p>
          )}
        </div>

        {/* Resume Score Card */}
        <div className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow"
             data-testid="resume-score-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Resume Score</h3>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Uploaded Resume</span>
          </div>

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className={`text-4xl font-bold ${hasResume ? scoreTextColor(resumeScore) : 'text-slate-300'}`}
                    data-testid="resume-score-value">
                {hasResume ? resumeScore : '—'}
              </span>
              {!hasResume && hasResumeUploaded && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid="resume-uploaded-badge">
                  Uploaded
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {hasResume ? (
                <>
                  <button
                    onClick={onRescoreResume}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-[#2563eb] rounded-lg text-xs font-semibold transition-colors"
                    data-testid="rescore-resume-btn"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Re-score Resume
                  </button>
                </>
              ) : hasResumeUploaded ? (
                <button
                  onClick={onRescoreResume}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg text-xs font-semibold transition-colors"
                  data-testid="score-resume-btn"
                >
                  <BarChart3 className="w-3.5 h-3.5" /> Score Resume
                </button>
              ) : (
                <button
                  onClick={onUploadResume}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg text-xs font-semibold transition-colors"
                  data-testid="upload-resume-btn-scoring"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload Resume
                </button>
              )}
            </div>
          </div>

          {hasResume && resumeBreakdown?.oneliner && (
            <p className="text-xs text-slate-500 italic leading-relaxed">
              {resumeBreakdown.oneliner}
            </p>
          )}
          {!hasResume && !hasResumeUploaded && (
            <p className="text-xs text-slate-400">
              Upload a resume to unlock skill-by-skill scoring with detailed breakdown.
            </p>
          )}
        </div>
      </div>

      {/* ── ZONE 2 — Resume Breakdown (only when scored) ────────────────── */}
      {hasResume && resumeBreakdown && (
        <div className="border border-slate-100 rounded-xl p-5 bg-slate-50/50 mb-4"
             data-testid="resume-breakdown-section">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Resume Breakdown</h3>

          {/* Dimension bars */}
          {Array.isArray(resumeBreakdown?.dimensions) && resumeBreakdown.dimensions.length > 0 && (
            <div className="space-y-3 mb-4">
              {resumeBreakdown.dimensions.map((dim, idx) => {
                const pct = dim.weight > 0
                  ? Math.round((dim.weighted_score / dim.weight) * 100)
                  : 0;
                return (
                  <div key={idx} data-testid={`dim-bar-${dim.name}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">{dim.name}</span>
                      <span className="text-xs font-semibold text-slate-700">
                        {dim.raw_score ?? dim.weighted_score ?? 0}
                        <span className="text-slate-400 font-normal"> / {dim.weight}</span>
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${scoreBarColor(pct)}`}
                        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {resumeSummary && (
            <p className="text-xs text-slate-600 italic leading-relaxed mb-3">
              {resumeSummary}
            </p>
          )}

          {/* Flags */}
          {Array.isArray(resumeFlags) && resumeFlags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {resumeFlags.map((flag, i) => (
                <span key={flag}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200"
                      data-testid={`resume-flag-${i}`}>
                  {flag}
                </span>
              ))}
            </div>
          )}

          {/* Recommendation */}
          {resumeRecommendation && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${recBadgeClass(resumeRecommendation)}`}
                  data-testid="resume-recommendation-badge">
              {resumeRecommendation}
            </span>
          )}
        </div>
      )}

      {/* ── ZONE 3 — LinkedIn Breakdown (only when scored) ──────────────── */}
      {hasLinkedin && linkedinBreakdown && (
        <div className="border border-slate-100 rounded-xl p-5 bg-slate-50/50"
             data-testid="linkedin-breakdown-section">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">LinkedIn Breakdown</h3>
          {linkedinBreakdown?.oneliner && (
            <p className="text-xs text-slate-500 italic leading-relaxed">
              {linkedinBreakdown.oneliner}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
